import React, { useState, useEffect } from "react";
import { db } from "../../../../services/firebaseService/connection";
import { updateDoc, doc } from "@firebase/firestore";
import { invalidateStadiumCache, postUpdateCDNUtil } from "../../../../helperFunctions/util";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import DescriptionText from "../../../../CommonComponents/DescriptionText";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import logger from "../../../../utils/logger";
import { toast } from "react-toastify";
import { isEmailValid } from "../../../../utils/validation";
import { ChantCheckbox, HelpTooltip } from "../../../../CommonComponents";
import ChantInput from "../../../../CommonComponents/ChantInput/ChantInput";
import "./AppSetting.css";
import { useForm } from "react-hook-form";
import { refreshGroupData } from "../../../../redux/actions/adminData";
import { Divider } from "antd";
import { ChatStreamSelector } from "./ChatStreamSelector";

const GeneralAppSettings = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [isSaving, setIsSaving] = useState(false);

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      supportEmail: "",
      showChatChannels: false,
      lockMemberSince: false,
      lockMemberName: false,
      allowCreatePrivateChannels: false,
      enableChatPostSharing: false,
      showTicketsNavigation: true,
      allowMembersToPostIndividualTickets: true,
      allowMembersToPostInChatStream: true,
      matchThreadChannelId: null,
      showFanProfilesInDirectory: true,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!groupData?.id) return;

    try {
      reset({
        showChatChannels: groupData?.appSettings?.showChatChannels,
        allowCreatePrivateChannels:
          groupData?.appSettings?.allowCreatePrivateChannels,
        enableChatPostSharing:
          groupData?.appSettings?.enableChatPostSharing !== false,

        supportEmail: groupData?.appSettings?.supportEmail,
        lockMemberSince: groupData?.appSettings?.lockMemberSince,
        lockMemberName: groupData?.appSettings?.lockMemberName,
        showFanProfilesInDirectory:
          groupData?.appSettings?.showFanProfilesInDirectory !== false,
        showTicketsNavigation:
          groupData?.appSettings?.showTicketsNavigation !== false,
        allowMembersToPostIndividualTickets:
          groupData?.appSettings?.allowMembersToPostIndividualTickets !== false,
        allowMembersToPostInChatStream:
          groupData?.appSettings?.allowMembersToPostInChatStream !== false,

        matchThreadChannelId: groupData?.matchThreadChannelId ?? null,
      });
    } catch (error) {
      logger.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const updateAppSettings = async (appSettingsData, groupData) => {
    if (!groupData?.id) throw new Error("Group ID is required");

    const firestoreUpdateData = {};

    if (appSettingsData.supportEmail !== undefined) {
      const email = appSettingsData.supportEmail?.trim();
      if (!email) throw new Error("Support Email is required");
      if (!isEmailValid(email)) throw new Error("Support Email is not valid");
      firestoreUpdateData["appSettings.supportEmail"] = email;
    }

    // eslint-disable-next-line no-unused-vars
    const { supportEmail, ...rest } = appSettingsData;
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== undefined) {
        firestoreUpdateData[`appSettings.${key}`] = value;
      }
    });

    if (Object.keys(firestoreUpdateData).length === 0) return;

    await updateDoc(doc(db, "group", groupData.id), firestoreUpdateData);
  };

  const updateMatchThreadChannelId = async (
    matchThreadChannelId,
    groupData,
  ) => {
    if (!groupData?.id) throw new Error("Group ID is required");

    await updateDoc(doc(db, "group", groupData.id), { matchThreadChannelId });
  };

  const saveDetails = async (data) => {
    try {
      setIsSaving(true);

      const updatedData = {};
      Object.keys(dirtyFields).forEach((key) => {
        if (data[key] !== undefined) updatedData[key] = data[key];
      });

      if (Object.keys(updatedData).length === 0) {
        toast.info("No changes to save");
        return;
      }

      const { matchThreadChannelId, ...appSettingsData } = updatedData;

      const updates = [];

      if (Object.keys(appSettingsData).length > 0) {
        updates.push(updateAppSettings(appSettingsData, groupData));
      }

      if (dirtyFields.matchThreadChannelId) {
        updates.push(
          updateMatchThreadChannelId(matchThreadChannelId, groupData),
        );
      }

      await Promise.all(updates);

      toast.success("Settings updated successfully");
      reset(data, { keepValues: true });
      postUpdateCDNUtil(groupData?.id);
      invalidateStadiumCache(groupData?.groupKey);
      refreshGroupData(groupData?.id);
    } catch (error) {
      logger.error("Error saving settings", error);
      toast.error(
        error.message || "Failed to save settings. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <DescriptionText showBackground>
        Apply global app settings to be reflected for all members. These
        settings are, primarily, meant to be set for the life of the group.
      </DescriptionText>

      <div className="app-settings-container">
        <div className="checkbox-section">
          <p className="checkbox-section-title">
            Member Support <span style={{ color: "#ff4d4f" }}> *</span>
          </p>

          <div className="checkbox-grid">
            <ChantInput
              control={control}
              name="supportEmail"
              style={{ maxWidth: "300px" }}
              label="Displayed in app settings and Leaders section of Member List providing a way for members to contact the group leaders."
              placeholder="Support Email"
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address",
                },
              }}
            />
          </div>
        </div>

        {/*chat settings */}
        <Divider style={{ margin: "16px 0" }} />

        <div className="checkbox-section">
          <p
            className="checkbox-section-title"
            style={{ paddingBottom: "12px" }}
          >
            Chat / Sharing
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <ChantCheckbox
              control={control}
              name="allowMembersToPostInChatStream"
              label="Allow members to post in chat stream?"
            />
            <ChantCheckbox
              control={control}
              name="showChatChannels"
              label="Enable chat channels (like Discord/Slack)?"
              onChange={(e) => {
                setValue("showChatChannels", e.target.checked, {
                  shouldDirty: true,
                });
                if (!e.target.checked) {
                  setValue("allowCreatePrivateChannels", false, {
                    shouldDirty: true,
                  });
                }
              }}
            />
            <ChantCheckbox
              disabled={!watch("showChatChannels")}
              control={control}
              name="allowCreatePrivateChannels"
              label="Allow members to create private Channels?"
            />

            <ChantCheckbox
              control={control}
              name="enableChatPostSharing"
              label="Allow members to 'Share' Stream/Channel posts outside of Chant?"
            />

            <div style={{ marginTop: "16px" }}>
              <ChatStreamSelector
                value={watch("matchThreadChannelId")}
                onChange={(value) =>
                  setValue("matchThreadChannelId", value, { shouldDirty: true })
                }
              />
            </div>
          </div>
        </div>

        <Divider style={{ margin: "16px 0" }} />
        <div className="checkbox-section">
          <p
            className="checkbox-section-title"
            style={{ paddingBottom: "12px" }}
          >
            Membership
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <ChantCheckbox
              control={control}
              name="lockMemberSince"
              label="Lock 'Member Since' field? (members unable to edit year)"
            />

            <ChantCheckbox
              control={control}
              name="lockMemberName"
              label="Lock 'Member Name' field? (members unable to edit name)"
            />

            {groupData?.regCodeRole === "fan" && (
              <ChantCheckbox
                control={control}
                name="showFanProfilesInDirectory"
                label={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>Show Fan (non-member) profiles in Directory</span>
                    <HelpTooltip text="Note: Members will be offered a toggle to show Fan profiles (off by default). Fans only allowed to see other Fans." />
                  </div>
                }
              />
            )}
          </div>
        </div>

        <Divider style={{ margin: "16px 0" }} />

        <div className="checkbox-section">
          <p
            className="checkbox-section-title"
            style={{ paddingBottom: "12px" }}
          >
            Tickets
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <ChantCheckbox
              control={control}
              name="showTicketsNavigation"
              label="Show Tickets in app navigation? (admin can post group tickets -- match, bus, event, etc.)"
              onChange={(e) => {
                setValue("showTicketsNavigation", e.target.checked, {
                  shouldDirty: true,
                });
                if (!e.target.checked) {
                  setValue("allowMembersToPostIndividualTickets", false, {
                    shouldDirty: true,
                  });
                }
              }}
            />

            <ChantCheckbox
              disabled={!watch("showTicketsNavigation")}
              control={control}
              name="allowMembersToPostIndividualTickets"
              label="Allow members to post individual tickets?"
            />
          </div>
        </div>

        <div className="footer ">
          <Divider style={{ margin: "16px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ChantButton
              onClick={handleSubmit(saveDetails)}
              loading={isSaving}
              disabled={!Object.keys(dirtyFields).length || isSaving}
            >
              Save
            </ChantButton>
          </div>
        </div>
      </div>
    </div>
  );
};
export default GeneralAppSettings;
